<template>
  <div>
    <a-card title="对账报表导出">
      <p class="hint muted">
        按<strong>业务时间</strong>（录入缴费业务日期）或<strong>销账时间</strong>（账单 paid_at）查询水电与物业费对账明细，并可导出 Excel。
      </p>
      <a-form layout="inline" class="filter-form">
        <a-form-item label="查询方式">
          <a-radio-group v-model="mode">
            <a-radio-button value="business_date">业务时间</a-radio-button>
            <a-radio-button value="paid_at">销账时间</a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item label="开始日期">
          <a-date-picker v-model="startDate" valueFormat="YYYY-MM-DD" placeholder="开始日期" />
        </a-form-item>
        <a-form-item label="结束日期">
          <a-date-picker v-model="endDate" valueFormat="YYYY-MM-DD" placeholder="结束日期" />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" :loading="loading" @click="onQuery">查询</a-button>
          <a-button style="margin-left: 8px" @click="onReset">重置</a-button>
          <a-button
            style="margin-left: 8px"
            type="default"
            :loading="exporting"
            :disabled="!items.length"
            @click="onExport"
          >
            导出报表
          </a-button>
        </a-form-item>
      </a-form>

      <a-table
        style="margin-top: 16px"
        size="small"
        :columns="columns"
        :dataSource="items"
        :rowKey="rowKey"
        :loading="loading"
        :pagination="pagination"
        @change="tableChange"
      />
    </a-card>
  </div>
</template>

<script>
import moment from 'moment'
import { reconciliationReportList, reconciliationReportExport } from '@/api/ownerPayment'

export default {
  name: 'ReconciliationExport',
  data() {
    return {
      mode: 'business_date',
      startDate: null,
      endDate: null,
      items: [],
      allItems: [],
      loading: false,
      exporting: false,
      page: 1,
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: (t) => `共 ${t} 条`,
      },
      columns: [
        { title: '业务日期', dataIndex: '业务日期', key: '业务日期', width: 120 },
        { title: '单位名称', dataIndex: '单位名称', key: '单位名称', ellipsis: true },
        { title: '房号', dataIndex: '房号', key: '房号', width: 100 },
        { title: '内容', dataIndex: '内容', key: '内容', width: 80 },
        { title: '起始日期', dataIndex: '起始日期', key: '起始日期', width: 120 },
        { title: '终止日期', dataIndex: '终止日期', key: '终止日期', width: 120 },
        { title: '付款方式', dataIndex: '付款方式', key: '付款方式', width: 100 },
        {
          title: '收入',
          dataIndex: '收入',
          key: '收入',
          width: 100,
          customRender: (t) => (t != null && t !== '' ? `¥${Number(t).toFixed(2)}` : '-'),
        },
        { title: '备注', dataIndex: '备注', key: '备注', ellipsis: true, customRender: (t) => t || '-' },
      ],
    }
  },
  methods: {
    rowKey(record, index) {
      return `${record['业务日期']}-${record['房号']}-${record['内容']}-${record['收入']}-${index}`
    },
    onQuery() {
      if (!this.startDate || !this.endDate) {
        this.$message.warning('请选择开始日期与结束日期')
        return
      }
      if (moment(this.startDate).isAfter(moment(this.endDate))) {
        this.$message.warning('开始日期不能晚于结束日期')
        return
      }
      this.page = 1
      this.pagination.current = 1
      this.fetchList()
    },
    onReset() {
      this.mode = 'business_date'
      this.startDate = null
      this.endDate = null
      this.items = []
      this.allItems = []
      this.page = 1
      this.pagination.current = 1
      this.pagination.total = 0
    },
    fetchList() {
      if (!this.startDate || !this.endDate) return
      this.loading = true
      reconciliationReportList({
        mode: this.mode,
        start: this.startDate,
        end: this.endDate,
      })
        .then((data) => {
          this.allItems = data.results || []
          this.pagination.total = data.count || this.allItems.length
          this.applyPage()
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    applyPage() {
      const size = this.pagination.pageSize
      const start = (this.pagination.current - 1) * size
      this.items = this.allItems.slice(start, start + size)
    },
    tableChange(pag) {
      this.page = pag.current
      this.pagination.current = pag.current
      if (pag.pageSize !== this.pagination.pageSize) {
        this.pagination.pageSize = pag.pageSize
        this.pagination.current = 1
        this.page = 1
      }
      this.applyPage()
    },
    async onExport() {
      if (!this.startDate || !this.endDate) {
        this.$message.warning('请先选择日期并查询')
        return
      }
      this.exporting = true
      try {
        const blob = await reconciliationReportExport({
          mode: this.mode,
          start: this.startDate,
          end: this.endDate,
        })
        const url = window.URL.createObjectURL(new Blob([blob]))
        const a = document.createElement('a')
        a.href = url
        a.download = `对账报表_${this.mode}_${this.startDate}_${this.endDate}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error(e?.response?.data?.detail || e?.message || '导出失败')
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped>
.hint {
  margin-bottom: 12px;
}
.muted {
  color: rgba(0, 0, 0, 0.45);
}
.filter-form {
  margin-bottom: 8px;
}
</style>
